import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import SectionLabel from "../SectionLabel";

/**
 * Brand-strip on the home page. Twelve manufacturer logos rendered as
 * monochrome white at 60 % opacity (forced via `filter: brightness(0)
 * invert(1)`), with no card chrome — they sit directly on the section
 * surface. Hover lifts each logo to full opacity.
 */
const LOGOS = [
    { slug: "control4", name: "Control4" },
    { slug: "savant", name: "Savant" },
    { slug: "josh-ai", name: "Josh.ai" },
    { slug: "lutron", name: "Lutron" },
    { slug: "coelux", name: "CoeLux" },
    { slug: "sonos", name: "Sonos" },
    { slug: "bang-olufsen", name: "Bang & Olufsen" },
    { slug: "k-array", name: "K-Array" },
    { slug: "sony", name: "Sony" },
    { slug: "kaleidescape", name: "Kaleidescape" },
    { slug: "samsung", name: "Samsung" },
    { slug: "ubiquiti", name: "Ubiquiti" },
];

export default function BrandsWall() {
    return (
        <section
            className="relative section-pad bg-[#0B0B0C] border-t border-[rgba(201,168,106,0.12)] overflow-hidden"
            data-testid="brands-wall"
        >
            <div className="shell">
                <div className="flex flex-col items-center text-center mb-14 md:mb-20">
                    <SectionLabel
                        number="03"
                        label="Trusted Partners"
                        align="center"
                    />
                    <h2 className="mt-8 font-serif text-4xl md:text-5xl lg:text-6xl max-w-3xl leading-[1.08]">
                        {"Brands we integrate, "}
                        <span className="italic text-[#C9A86A]">
                            certified to install.
                        </span>
                    </h2>
                    <p className="mt-6 text-[#9A958A] max-w-xl leading-[1.85]">
                        Our studio holds current manufacturer credentials with
                        every partner below — and specifies only what the
                        residence genuinely requires.
                    </p>
                </div>

                <div
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-6 gap-y-8 md:gap-x-10 md:gap-y-10 lg:gap-x-12 lg:gap-y-12 items-center"
                    data-testid="brands-strip"
                >
                    {LOGOS.map(({ slug, name }, idx) => (
                        <motion.div
                            key={slug}
                            suppressHydrationWarning
                            initial={{ opacity: 0, y: 10 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{
                                duration: 0.6,
                                delay: idx * 0.04,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            className="flex items-center justify-center"
                        >
                            <Link
                                to={`/brands/${slug}`}
                                aria-label={`Explore ${name} — Trusted Partner`}
                                data-testid={`brand-logo-${slug}`}
                                className="group flex items-center justify-center w-full py-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#C9A86A] focus-visible:ring-offset-4 focus-visible:ring-offset-[#0B0B0C]"
                            >
                                <img
                                    src={`/images/logos/${slug}.png`}
                                    alt={`${name} logo`}
                                    loading="lazy"
                                    className="h-12 md:h-16 lg:h-20 xl:h-24 w-auto object-contain opacity-60 transition-opacity duration-300 ease-out group-hover:opacity-100"
                                    style={{
                                        filter: "brightness(0) invert(1)",
                                    }}
                                />
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}
